<template>
    <div>
        <ModalBasic
            :title="'Invite User'"
            :modal-open="modals.invite.state.isOpen"
            @close-modal="modals.invite.state.isOpen = false"
        >
            <FormKit v-slot="{ state: inviteUserFormState }" type="group">
                <div
                    class="p-6"
                    :class="{
                        'pointer-events-none opacity-50': modals.invite.state.is_busy,
                    }"
                >
                    <p class="mb-8 text-sm">
                        This will send an email to the address you specify below with an invitation to join your
                        organization.
                    </p>

                    <div class="mb-4 text-sm">
                        <div class="flex gap-6">
                            <div class="flex-1 items-center">
                                <FormKit
                                    v-model="modals.invite.values.first_name"
                                    type="text"
                                    name="first-name"
                                    label="First Name"
                                    validation="required:trim"
                                    validation-visibility="dirty"
                                />
                            </div>
                            <div class="flex-1 items-center">
                                <FormKit
                                    v-model="modals.invite.values.last_name"
                                    type="text"
                                    name="last-name"
                                    label="Last Name"
                                    validation="required:trim"
                                    validation-visibility="dirty"
                                />
                            </div>
                        </div>

                        <div class="my-4">
                            <FormKit
                                v-model="modals.invite.values.email"
                                type="email"
                                name="email"
                                label="Email"
                                help="Enter user email"
                                validation="required|email"
                                validation-visibility="dirty"
                            />
                        </div>

                        <div class="my-4">
                            <FormKit
                                v-model="modals.invite.values.organization_operator.role"
                                type="select"
                                name="role"
                                label="Role"
                                placeholder="Select a role"
                                validation="required"
                                :options="inferiorRoles"
                            />
                        </div>
                    </div>
                </div>

                <div class="border-t border-slate-200 px-5 py-4">
                    <div class="flex flex-wrap justify-end space-x-2">
                        <button
                            class="btn-sm border-slate-200 text-slate-600 hover:border-slate-300"
                            @click="onModalClose('invite')"
                        >
                            Cancel
                        </button>

                        <button
                            class="btn-sm"
                            :class="{
                                'pointer-events-none bg-slate-50 text-slate-400': !inviteUserFormState.valid,
                                'bg-indigo-500 text-white hover:bg-indigo-600': inviteUserFormState.valid,
                            }"
                            :disable="!inviteUserFormState.valid"
                            @click="onModalSubmit('invite')"
                        >
                            Invite
                        </button>
                    </div>
                </div>
            </FormKit>
        </ModalBasic>

        <ModalBasic
            :title="'Edit User'"
            :modal-open="modals.edit.state.isOpen"
            @close-modal="modals.edit.state.isOpen = false"
        >
            <div
                class="p-6"
                :class="{
                    'pointer-events-none opacity-50': modals.edit.state.is_busy,
                }"
            >
                <div class="mb-4 text-sm">
                    <div class="flex items-center gap-6">
                        <div class="flex-1">
                            <label class="mb-2 block text-sm font-medium">First Name</label>
                            <input v-model="modals.edit.values.first_name" type="text" class="form-input w-full" />
                        </div>
                        <div class="flex-1">
                            <label class="mb-2 block text-sm font-medium">Last Name</label>
                            <input v-model="modals.edit.values.last_name" type="text" class="form-input w-full" />
                        </div>
                    </div>

                    <div class="my-4">
                        <FormKit
                            v-model="modals.edit.values.email"
                            type="email"
                            name="email"
                            label="Email"
                            validation="required|email"
                            validation-visibility="dirty"
                        />
                    </div>

                    <!-- <div class="my-4">
                    <label class="block text-sm font-medium mb-2">Email</label>
                    <input
                        type="text"
                        class="form-input w-full"
                        v-model="modals.edit.values.email"
                        placeholder="Enter user email"
                    />
                </div> -->

                    <div v-if="selfOrganizationRole && selfOrganizationRole.key === 'superadmin'" class="my-4">
                        <label class="mb-2 block text-sm font-medium">Role</label>
                        <FormKit
                            v-model="modals.edit.values.organization_operator.role"
                            type="select"
                            :options="inferiorRoles"
                        />
                    </div>

                    <div v-if="modals.edit.values.is_email_verified !== true">
                        <label v-if="modals.edit.state.reset" class="mb-2 block text-sm font-medium"
                            >Invitation last sent
                            {{ $filters.formatDateAndTime(modals.edit.state.reset.created_at) }}</label
                        >
                        <label v-if="!modals.edit.state.reset" class="mb-2 block text-sm font-medium"
                            >No active invitation, please send again.</label
                        >
                        <button
                            v-if="canResendVerification"
                            class="btn-sm bg-indigo-500 text-white hover:bg-indigo-600"
                            @click="onResendVerification()"
                        >
                            Send invite
                        </button>
                        <div
                            v-if="modals.edit.state.reset"
                            class="group relative mt-3 flex cursor-pointer items-center rounded bg-slate-100 p-2 text-xs font-medium hover:bg-slate-200"
                            @click="copyVerifyToClipboard($event, verificationLink)"
                        >
                            <span
                                class="max-w-96 overflow-hidden leading-none text-slate-500 group-hover:text-slate-700"
                                >Copy verification url</span
                            >
                            <AppIcon
                                name="copy"
                                class="mb-0.5 ml-1 ml-auto h-3 w-3 text-slate-500 group-hover:text-slate-700"
                            />
                            <span
                                class="overlay-message absolute inset-0 flex items-center justify-center rounded bg-black bg-opacity-80 text-white opacity-0 transition-opacity duration-300"
                            >
                                <span class="pointer-events-none">Copied to clipboard!</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-between border-t border-slate-200 px-5 py-4">
                <div>
                    <button
                        class="btn-sm bg-rose-500 text-white hover:bg-rose-600"
                        @click="onOperatorAction({ type: 'delete', operator: { id: modals.edit.state.operatorId } })"
                    >
                        Remove
                    </button>
                </div>
                <div class="flex flex-wrap justify-end space-x-2">
                    <button
                        class="btn-sm border-slate-200 text-slate-600 hover:border-slate-300"
                        @click="onModalClose('edit')"
                    >
                        Cancel
                    </button>

                    <button class="btn-sm bg-indigo-500 text-white hover:bg-indigo-600" @click="onModalSubmit('edit')">
                        Save
                    </button>
                </div>
            </div>
        </ModalBasic>

        <ModalBasic
            :title="'Remove User'"
            :modal-open="modals.delete.state.isOpen"
            @close-modal="modals.delete.state.isOpen = false"
        >
            <div
                class="p-6"
                :class="{
                    'pointer-events-none opacity-50': modals.delete.state.is_busy,
                }"
            >
                <div class="mb-4 text-sm">
                    <p>
                        Are you sure you want to remove this user from
                        <span class="font-semibold">{{ organization.name }}</span
                        >? They will no longer be able to access any of your organization's campaigns.
                    </p>
                </div>
            </div>

            <div class="border-t border-slate-200 px-5 py-4">
                <div class="flex flex-wrap justify-end space-x-2">
                    <button
                        class="btn-sm border-slate-200 text-slate-600 hover:border-slate-300"
                        @click="onModalClose('delete')"
                    >
                        Cancel
                    </button>

                    <button class="btn-sm bg-rose-500 text-white hover:bg-rose-600" @click="onModalSubmit('delete')">
                        Remove
                    </button>
                </div>
            </div>
        </ModalBasic>

        <div class="space-y-6">
            <header class="flex flex-col md:flex-row md:items-start md:justify-between">
                <div>
                    <Heading
                        title="User Management"
                        :subtitle="selfOrganizationRole && ['superadmin', 'admin'].includes(selfOrganizationRole.key) ? 'Manage users in your organization and their permissions.' : ''"
                    />
                </div>

                <!-- v-if="selfOrganizationRole && ['superadmin', 'admin'].includes(selfOrganizationRole.key)" -->
                <div
                    v-if="isSfStaff"
                >
                    <Tooltip :show-tooltip="hasReachedSeatLimit" class="mt-2 md:mt-0">
                        <template #trigger>
                            <Button
                                :disabled="hasReachedSeatLimit"
                                class="w-full md:w-auto"
                                @click="modals.invite.state.isOpen = true"
                            >
                                <AppIcon name="user-plus" class="h-4 w-4 mr-2" />
                                <span>Invite User</span>
                            </Button>
                        </template>
                        <template #default>
                            Your current plan allows up to {{ currentSeatLimit }} users.
                        </template>
                    </Tooltip>
                </div>
            </header>

            <div v-for="(role, roleKey) in roles" :key="roleKey" class="mb-6">
                <Card class="rounded">
                    <CardHeader>
                        <CardTitle>
                            {{ getFromDictionary(`organization.role.${roleKey}`, 'labelPlural') }}
                        </CardTitle>
                        <p class="text-sm text-muted-foreground">
                            {{ getFromDictionary(`organization.role.${roleKey}`, 'description') }}
                        </p>
                    </CardHeader>

                    <CardContent>
                        <div class="overflow-x-auto">
                            <SkeletonOperatorTable v-if="!state.isLoaded" />

                            <OperatorsTable
                                v-else-if="state.isLoaded && role.operators.length"
                                :operators="role.operators"
                                @action="onOperatorAction"
                            />

                            <div
                                v-else-if="state.isLoaded && role.operators.length === 0"
                                class="m-auto text-sm text-center md:text-left text-slate-500"
                            >
                                No {{ getFromDictionary(`organization.role.${roleKey}`, 'labelPlural') }} in your organization
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { notify } from '@kyvg/vue3-notification'
import operatorAPI from '@/utils/api/api.operator'
import moment from 'moment'
import Tooltip from '@/components/Tooltip.vue'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/typography'
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card'
import ModalBasic from '../components/ModalBasic.vue'
import OperatorsTable from '../partials/tables/OperatorsTable.vue'
import SkeletonOperatorTable from '../partials/skeletons/SkeletonOperatorTable.vue'

export default {
    name: 'OrganizationManagementSubpage',

    components: {
        Button,
        ModalBasic,
        OperatorsTable,
        SkeletonOperatorTable,
        Tooltip,
        Heading,
        Card,
        CardHeader,
        CardContent,
        CardTitle,
    },

    props: {
        organization: {
            type: Object,
        },
    },

    data() {
        return {
            roles: {
                superadmin: {
                    key: 'superadmin',
                    operators: [],
                    order: 0,
                },
                admin: {
                    key: 'admin',
                    operators: [],
                    order: 1,
                },
                user: {
                    key: 'user',
                    operators: [],
                    order: 2,
                },
            },

            selfOrganizationRole: null,
            selectedOperator: null,
            state: {
                isLoaded: false,
            },
            operatorCount : 0,

            modals: {
                invite: {
                    state: {
                        isOpen: false,
                        is_busy: false,
                    },
                    values: {},
                    defaultValues: {
                        email: '',
                        first_name: '',
                        last_name: '',
                        organization_operator: {
                            role: 'user',
                        },
                    },
                },
                edit: {
                    state: {
                        isOpen: false,
                        is_busy: false,
                        operatorId: null,
                    },
                    values: {},
                    defaultValues: {
                        first_name: '',
                        last_name: '',
                        email: '',
                        organization_operator: {
                            role: 'user',
                        },
                        isEmailVerified: false,
                        organizationId: null,
                    },
                },
                delete: {
                    state: {
                        isOpen: false,
                        is_busy: false,
                        operatorId: null,
                    },
                },
            },
        }
    },

    computed: {
        ...mapGetters(['selfOperator', 'selfActiveOrganizationId', 'getFromDictionary', 'isSfStaff', 'selfActiveOrganization']),

        inferiorRoles() {
            if (!this.selfOrganizationRole) return []

            const mappedRoles = _(this.roles)
                .map((role, roleKey) => ({
                    label: this.getFromDictionary(`organization.role.${roleKey}`, 'label'),
                    value: roleKey,
                    order: role.order,
                }))
                .orderBy(['order', 'asc'])
                .value()

            if (this.isSfStaff || this.selfOrganizationRole.key === 'superadmin') {
                return mappedRoles
            }

            return _.filter(mappedRoles, (role) => role.order > this.selfOrganizationRole.order)
        },

        canResendVerification() {
            if (!this.modals.edit.state.reset) {
                return true
            }

            const createdAt = moment.utc(this.modals.edit.state.reset.created_at)
            const duration = moment.utc().diff(createdAt, 'minutes')
            return duration >= 5
        },

        verificationLink() {
            if (!this.modals.edit.state.reset) {
                return ''
            }

            return this.modals.edit.state.reset.url
        },

        currentSeatLimit() {
            return Math.max(this.selfActiveOrganization.seats, 1)
        },

        hasReachedSeatLimit() {
            return !this.state.isLoaded || ( !(this.selfActiveOrganization.is_legacy_billing === true) && this.operatorCount >= this.currentSeatLimit)
        }
    },

    created() {
        this.getSelfOrganizationRole()
        this.initializeModals()
        this.getOrganization()
    },

    methods: {
        getSelfOrganizationRole() {
            // Assume user by default
            let selfRole = this.roles.user

            // SF Staff is auto superadmin
            if (this.isSfStaff) {
                selfRole = this.roles.superadmin
            } else if (this.selfOperator.organizations) {
                const currentOrganization = _.find(
                    this.selfOperator.organizations,
                    (organization) => organization.id === this.selfActiveOrganizationId,
                )

                if (currentOrganization) {
                    selfRole = this.roles[currentOrganization.organization_operator.role]
                }
            }

            this.selfOrganizationRole = selfRole
        },

        getOrganization() {
            this.$axios
                .get(`/organizations/${this.selfActiveOrganizationId}`, {
                    params: {
                        relations: ['operators', 'operator-reset'],
                    },
                })
                .then(({ data }) => {

                    this.operatorCount = 0

                    _.forEach(this.roles, (role) => {
                        role.operators = []
                    })

                    _.forEach(data.payload.organization.operators, (operator) => {
                        const roleEntry = this.roles[operator.organization_operator.role]

                        const isSelf = operator.id === this.selfOperator.id

                        operator.custom = {
                            is_self: isSelf,
                            is_editable: this.isSfStaff || this.selfOrganizationRole.key === 'superadmin',
                        }

                        roleEntry.operators.push(operator)

                        this.operatorCount += 1
                    })

                    this.state.isLoaded = true
                })
                .catch((error) => {
                    console.log(error)
                })
        },

        resetModalValues(modalKey) {
            if (_.has(this.modals[modalKey], 'defaultValues')) {
                this.modals[modalKey].values = _.cloneDeep(this.modals[modalKey].defaultValues)
            }
        },

        onModalClose(modalKey) {
            this.modals[modalKey].state.isOpen = false
        },

        initializeModals() {
            _.forEach(this.modals, (modal, modalKey) => {
                if (modal.defaultValues) {
                    this.resetModalValues(modalKey)
                }
            })
        },

        onOperatorAction({ type, operator }) {
            this.resetModalValues(type)

            this.selectedOperator = operator
            // Close all other modals
            _.forEach(this.modals, (modal) => {
                // eslint-disable-next-line no-param-reassign
                modal.state.isOpen = false

                if (modal.state.operatorId) {
                    this.modals[type].state.operatorId = null
                }
            })

            // If the modal has default values, populate them
            if (type === 'edit') {
                this.modals[type].values = _.cloneDeep(operator)
            }
            this.modals[type].state.isOpen = true
            this.modals[type].state.operatorId = operator.id
            this.modals[type].state.organiationId = operator?.organization_operator?.organization_id

            if (operator?.operator_resets?.length > 0) {
                this.modals[type].state.reset = operator.operator_resets[0]
            } else {
                this.modals[type].state.reset = null
            }
        },

        onModalSubmit(modalKey) {
            const modal = this.modals[modalKey]
            modal.state.is_busy = true

            if (modalKey === 'invite') {
                this.$axios
                    .post(`/organizations/${this.organization.id}/operators`, {
                        operator: modal.values,
                    })
                    .then(async () => {
                        await this.getOrganization()
                        modal.state.is_busy = false

                        this.onModalClose(modalKey)

                        notify({
                            title: 'Invitation successful',
                            text: 'An invitation has been sent to the users email address.',
                            type: 'success',
                        })
                    })
                    .catch((error) => {
                        modal.state.is_busy = false
                        notify({
                            title: 'Invite error',
                            text: 'Unable to invite user, please try again later.',
                            type: 'error',
                        })
                        console.log(error)
                    })
            }

            if (modalKey === 'edit') {
                this.$axios
                    .put(`/organizations/${this.organization.id}/operators/${modal.state.operatorId}`, {
                        operator: modal.values,
                    })
                    .then(async () => {
                        await this.getOrganization()
                        modal.state.is_busy = false

                        this.onModalClose(modalKey)

                        notify({
                            title: 'Saved successful',
                            text: 'User has been saved.',
                            type: 'success',
                        })
                    })
                    .catch((error) => {
                        modal.state.is_busy = false
                        notify({
                            title: 'Saving error',
                            text: 'Unable to save user, please try again later.',
                            type: 'error',
                        })
                        console.log(error)
                    })
            }

            if (modalKey === 'delete') {
                this.$axios
                    .delete(`/organizations/${this.organization.id}/operators/${modal.state.operatorId}`)
                    .then(async () => {
                        await this.getOrganization()
                        modal.state.is_busy = false

                        this.onModalClose(modalKey)

                        notify({
                            title: 'Deletion successful',
                            text: 'User has been deleted.',
                            type: 'success',
                        })
                    })
                    .catch((error) => {
                        modal.state.is_busy = false
                        notify({
                            title: 'Deletion error',
                            text: 'Unable to delete user, please try again later.',
                            type: 'error',
                        })
                        console.log(error)
                    })
            }
        },

        async onResendVerification() {
            this.onModalClose('edit')

            const response = await operatorAPI.resendVerification(
                this.$axios,
                this.modals.edit.state.organiationId,
                this.modals.edit.state.operatorId,
            )

            if (response && response.success) {
                this.selectedOperator.operator_resets = [response.value]
                this.modals.edit.state.reset = [response.value]
                notify({
                    title: 'Verification sent',
                    text: 'A verification message has been sent to the user.',
                    type: 'success',
                })
            } else {
                notify({
                    title: 'Verification error',
                    text: 'Unable to send a verification message to the user, please try again later.',
                    type: 'error',
                })
            }
        },

        async copyVerifyToClipboard(event, textToCopy) {
            try {
                await navigator.clipboard.writeText(textToCopy)

                const overlayMessage = event.target

                if (overlayMessage) {
                    overlayMessage.classList.add('opacity-100')

                    setTimeout(() => {
                        overlayMessage.classList.remove('opacity-100')
                    }, 2500)
                } else {
                    console.error('Overlay message element not found.')
                }
            } catch (err) {
                console.error('Failed to copy text: ', err)
            }
        },
    },
}
</script>
